import React, { FC, Key, ReactNode, useEffect, useMemo, useState } from 'react';
import { Button, Menu, Select, Table } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { CountBadge, EmptyState, InfiniteScroll, SearchInput, Spin } from 'common/components';
import { getFilterOptions } from 'common/helpers';
import {
  DEFAULT_EMPTY_VALUE,
  DEFAULT_LIST_OFFSET,
  DEFAULT_PAGINATION_PAGE,
  LIST_LIMIT_20,
  PREVIOUS_PATH_KEY,
  SELECT_LIST_HEIGHT_320,
} from 'common/config';
import { ArrowDownShort, ArrowUpShort } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ESupplyGroupType, ESupplyStatus } from 'entities/Supply/Supply.enum';
import { IGroupedSupplyListPayload } from 'entities/Supply/Supply.models';
import {
  getGroupedSuppliesCount,
  mapGroupedSupplyListToDataSource,
  renderGroupedSupplyListExpandedRowRecords,
  renderGroupedSupplyListRecords,
} from 'entities/Supply/Supply.helper';
import { EUserRole } from 'entities/User/User.enum';
import {
  getGroupedSupplyList,
  getGroupedSupplyListChunk,
  getSupplyAddresses,
  getSupplyBuyers,
  getSupplySubdivisions,
  getSupplyUsers,
  selectIsSupplyLoading,
  selectSupplyAddresses,
  selectSupplyBuyers,
  selectSupplyCount,
  selectSupplyGroupedList,
  selectSupplySubdivisions,
  selectSupplyUsers,
  setSupplyGroupedList,
} from 'entities/Supply/Supply.slice';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  role?: string;
}

export const SupplyListTab: FC<IComponentProps> = ({ header, role }) => {
  const dispatch = useAppDispatch();
  // supply
  const supplyCount = useAppSelector(selectSupplyCount);
  const isSupplyLoading = useAppSelector(selectIsSupplyLoading);
  const supplyBuyers = useAppSelector(selectSupplyBuyers);
  const supplySubdivisions = useAppSelector(selectSupplySubdivisions);
  const supplyUsers = useAppSelector(selectSupplyUsers);
  const supplyAddresses = useAppSelector(selectSupplyAddresses);
  const supplyGroupedList = useAppSelector(selectSupplyGroupedList);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
  const [payload, setPayload] = useState<IGroupedSupplyListPayload>({
    statuses: [ESupplyStatus.Approved, ESupplyStatus.Delivery],
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const location = useLocation();
  const navigate = useNavigate();

  const asManager = role === EUserRole.Manager;
  const asSellerManager = role === EUserRole.SellerManager;
  const buyerOptions = getFilterOptions.buyer(supplyBuyers);
  const subdivisionOptions = getFilterOptions.subdivision(supplySubdivisions);
  const userOptions = getFilterOptions.user(supplyUsers);
  const addressOptions = getFilterOptions.address(supplyAddresses);
  const suppliesCount = getGroupedSuppliesCount(supplyGroupedList);
  const dataSource = useMemo(() => mapGroupedSupplyListToDataSource(supplyGroupedList), [supplyGroupedList]);
  const columns = renderGroupedSupplyListRecords(role, currentUser?.account);

  const onBuyerChange = async (value: number) => {
    const newPayload: IGroupedSupplyListPayload = {
      ...payload,
      accountId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
      subdivisionIds: undefined,
      userId: undefined,
      addressId: undefined,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);

    await dispatch(
      getSupplySubdivisions({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
      }),
    );
    await dispatch(
      getSupplyUsers({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
      }),
    );
    await dispatch(
      getSupplyAddresses({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
      }),
    );
    dispatch(getGroupedSupplyList(newPayload));
  };

  const onSubdivisionChange = async (value: number) => {
    const newPayload: IGroupedSupplyListPayload = {
      ...payload,
      subdivisionIds: value === DEFAULT_EMPTY_VALUE ? undefined : [value],
      userId: undefined,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    await dispatch(
      getSupplyUsers({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
        subdivisionIds: newPayload.subdivisionIds,
      }),
    );

    if (asSellerManager) {
      await dispatch(
        getSupplyAddresses({
          statuses: newPayload.statuses,
          accountId: newPayload.accountId,
          subdivisionIds: newPayload.subdivisionIds,
        }),
      );
    }

    dispatch(getGroupedSupplyList(newPayload));
  };

  const onUserChange = (value: number) => {
    const newPayload: IGroupedSupplyListPayload = {
      ...payload,
      userId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    dispatch(getGroupedSupplyList(newPayload));
  };

  const onAddressChange = (value: number) => {
    const newPayload: IGroupedSupplyListPayload = {
      ...payload,
      addressId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    getGroupedSupplyList(newPayload);
  };

  const onSearchClick = async (value?: string) => {
    const newPayload: IGroupedSupplyListPayload = {
      ...payload,
      search: value,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    await dispatch(getGroupedSupplyList(newPayload));
  };

  const navigateToRequest = (path: string) => {
    sessionStorage.setItem(PREVIOUS_PATH_KEY, `${location.pathname}${location.search}`);
    navigate(path);
  };

  useEffect(() => {
    const fetch = async () => {
      dispatch(setSupplyGroupedList([]));

      if (asManager) {
        await dispatch(getSupplySubdivisions({ statuses: payload.statuses }));
        await dispatch(getSupplyUsers({ statuses: payload.statuses }));
      }

      if (asSellerManager) {
        // TODO remove string, add enum
        await dispatch(getSupplyBuyers({ statuses: payload.statuses, deliveryType: 'delivery' }));
      }

      await dispatch(getGroupedSupplyList(payload));

      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <InfiniteScroll
      loading={scrollLoading}
      hasMore={!isSupplyLoading && supplyGroupedList.length < supplyCount}
      loader={<Spin />}
      scrollToTopTrigger={[payload]}
      loadMore={async () => {
        setScrollLoading(true);
        setPage(page + 1);
        await dispatch(
          getGroupedSupplyListChunk({
            ...payload,
            offset: LIST_LIMIT_20 * page,
          }),
        );
        setScrollLoading(false);
      }}
    >
      {header}

      <Menu
        items={[
          {
            key: ESupplyStatus.Approved,
            label: (
              <>
                Ожидаемые поставки
                <CountBadge count={suppliesCount} />
              </>
            ),
          },
        ]}
        selectedKeys={[ESupplyStatus.Approved]}
        mode="horizontal"
      />

      <div className="supply-list__filters mb-7">
        {asSellerManager && (
          <Select
            rootClassName="redesign"
            className="request-list__filters-select"
            placeholder="Выберите компанию"
            listHeight={SELECT_LIST_HEIGHT_320}
            virtual={false}
            suffixIcon={<ArrowDownShort />}
            options={buyerOptions}
            value={payload?.accountId ? payload.accountId : DEFAULT_EMPTY_VALUE}
            onChange={onBuyerChange}
          />
        )}

        {(asManager || (asSellerManager && payload?.accountId)) && (
          <>
            <Select
              rootClassName="redesign"
              className="supply-list__filters-select"
              placeholder="Выберите подразделение"
              listHeight={SELECT_LIST_HEIGHT_320}
              virtual={false}
              suffixIcon={<ArrowDownShort />}
              options={subdivisionOptions}
              value={payload?.subdivisionIds ? payload.subdivisionIds[0] : DEFAULT_EMPTY_VALUE}
              onChange={onSubdivisionChange}
            />

            <Select
              rootClassName="redesign"
              className="supply-list__filters-select"
              placeholder="Выберите пользователя"
              listHeight={SELECT_LIST_HEIGHT_320}
              virtual={false}
              suffixIcon={<ArrowDownShort />}
              options={userOptions}
              value={payload?.userId ? payload.userId : DEFAULT_EMPTY_VALUE}
              onChange={onUserChange}
            />
          </>
        )}

        {asSellerManager && payload?.accountId && (
          <Select
            rootClassName="redesign"
            className="request-list__filters-select"
            placeholder="Выберите адрес"
            listHeight={SELECT_LIST_HEIGHT_320}
            virtual={false}
            suffixIcon={<ArrowDownShort />}
            options={addressOptions}
            value={payload?.addressId ? payload.addressId : DEFAULT_EMPTY_VALUE}
            onChange={onAddressChange}
          />
        )}

        <SearchInput placeholder="Поиск" onSearch={onSearchClick} />
      </div>

      <Table
        className={`supply-list__table ${!!supplyGroupedList.length && supplyGroupedList.length === supplyCount && 'table-end-of-list'} ${!supplyGroupedList.length ? 'table-empty' : ''}`}
        showHeader={false}
        pagination={false}
        dataSource={dataSource}
        columns={columns}
        rowClassName={(record) => {
          return expandedRowKeys.includes(record.key) ? 'expanded' : '';
        }}
        components={{
          body: {
            row: ({ children, ...props }: any) => {
              const row = props['data-row-key'];
              const record = dataSource.find((item) => item.key === row);

              if (record?.type === ESupplyGroupType.Date) {
                return (
                  <tr className="supply-list__table-date-row">
                    <td colSpan={columns.length + 1}>
                      <span className="text-controls-accent">{record.label}</span>
                    </td>
                  </tr>
                );
              }

              return <tr {...props}>{children}</tr>;
            },
          },
        }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (value) => {
            setExpandedRowKeys(value as Key[]);
          },
          expandedRowClassName: (record) => {
            return expandedRowKeys.includes(record.key) ? 'expanded' : '';
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            return (
              <div className="flex align-center justify-end">
                <Button
                  className={`button-circle secondary ${expanded ? 'expanded' : ''}`}
                  icon={expanded ? <ArrowUpShort className="icon-arrow-up-short-bright-green" /> : <ArrowDownShort />}
                  onClick={(e) => onExpand(record, e)}
                />
              </div>
            );
          },
          expandIconColumnIndex: 6,
          expandedRowRender: (props) => {
            return (
              <Table
                pagination={false}
                showHeader={false}
                dataSource={props.data?.supplies.map((supply) => ({ ...supply, key: supply.id }))}
                columns={renderGroupedSupplyListExpandedRowRecords(currentUser?.account, navigateToRequest)}
              />
            );
          },
        }}
        locale={{
          emptyText: fetchLoading ? (
            <Spin />
          ) : (
            <EmptyState
              title="В этом разделе нет поставок"
              description="Они появятся здесь после назначения даты доставки на стороне поставщика."
            />
          ),
        }}
      />
    </InfiniteScroll>
  );
};
